// external libraries
import { useMemo } from 'react';

// components
import { useDateNavigationStore } from 'components/shared/DateNavigation/DateNavigation.stores';
import { useDateNavigationHoursStore } from 'components/shared/DateNavigation/DateNavigationHours.stores';

// stores
import { useSelectedPlanUnits } from '../RosterPlan.stores';

// utils
import { getCostCenterIdsFromPlanUnits } from '../utils/getCostCenterIdsFromPlanUnits';
import { getPeriodBounds } from '../utils/getPeriodBounds';

/**
 * The one place the roster screen turns its selection into query input: the four roster queries
 * (employees, open requests, publish date, qualifications) all take the same `costCenters` plus
 * interval, and all four stay disabled until a plan unit is picked.
 *
 * `startDate` / `endDate` come back as dates next to the serialised parameters, because two
 * consumers need the interval itself rather than its ISO form: `useDatesToDisplay` builds the grid
 * columns from it, and the toolbar's plan unit selector scopes its list to it (the `interval`
 * pass-through from #2514). They are native `Date`s, not dayjs — the selector takes them straight.
 *
 * The result is memoized on the state it derives from, so the react-query keys keep a stable
 * identity between renders.
 */
const useRosterPlanQueryParams = () => {
  const selectedPlanUnits = useSelectedPlanUnits();
  const period = useDateNavigationStore((state) => state.period);
  const selectedDate = useDateNavigationHoursStore(
    (state) => state.selectedDate
  );

  /**
   * Explicit `useMemo` and no React Compiler directive, against the wider repo's habit. This
   * hook's own return identity is not load bearing; it follows the same rule as the other five so
   * the folder has one convention rather than a per-hook judgement call. Reasoning in README.md,
   * "Decisions".
   */
  return useMemo(() => {
    const costCenters = getCostCenterIdsFromPlanUnits(selectedPlanUnits);
    const { startDate, endDate } = getPeriodBounds({ period, selectedDate });

    return {
      params: {
        costCenters,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      },
      options: { enabled: !!costCenters },
      startDate,
      endDate
    };
  }, [period, selectedDate, selectedPlanUnits]);
};

export default useRosterPlanQueryParams;
